import React from 'react';
import styled, { css } from 'styled-components';
import { ChevronUp, ChevronDown } from 'lucide-react';

export const colors = {
  background: 'var(--white-a15)',
  backdrop: 'blur(18px)',
  border: 'var(--white-a66)',
  green: 'var(--green-800)',
  orange: 'var(--orange-500)',
  white: 'var(--white)',
  text: 'var(--grey-700)',
  shadow: 'var(--shadow-a08)',
  shadowHeavy: 'var(--shadow-a15)',
};

export const tableColors = {
  header: 'var(--green-800)',
  accent: 'var(--orange-500)',
  text: 'var(--black)',
};


export const TableHeaderBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem 1rem;
  padding: 0.75rem 1rem;
  background: var(--white-a25);
  border-radius: 12px;
  backdrop-filter: blur(8px);
`;

export const Container = styled.div`
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

export const TableWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: auto;
  scroll-behavior: smooth;
  position: relative;
`;

export const Table = styled.table`
  width: 100%;
  max-width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  font-family: var(--font-body);
  border-radius: 16px;
  overflow: hidden;
`;

export const Td = styled.td`
  padding: 0.5rem;
  text-align: ${({ numeric }) => (numeric ? 'right' : 'left')};
  vertical-align: middle;
  font-family: var(--font-body);
  background: var(--white-a20);
  backdrop-filter: blur(6px);
  color: ${tableColors.text};
  border-top: 1px solid var(--white-a25);
  border-bottom: 1px solid var(--white-a25);
  & > * { max-width: 100%; width: 100%; box-sizing: border-box; }
`;

export const StyledTr = styled.tr`
  transition: all 0.3s ease;
  background: ${({ $index }) =>
    $index % 2 ? 'var(--white-a25)' : 'var(--white-a15)'};
  &:hover { background-color: var(--white-a40); }
`;

export const SortableHeader = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  padding: 0.5rem;
  font-size: 0.875rem;
  text-align: left;
  background-color: ${tableColors.header};
  color: var(--white);
  font-family: var(--font-heading);
  border-bottom: 1px solid var(--white-a20);
  white-space: nowrap;
  &:first-child { border-top-left-radius: 12px; }
  &:last-child { border-top-right-radius: 12px; }
  svg { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); color: var(--white); opacity: 1; }
  &:hover svg { opacity: 1; }
  &:hover { background-color: var(--green-900); }
`;

export const SortArrow = ({ direction }) =>
  direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;

// ─── CustomerSpecsTableLayout (scoped copy of StandardTableLayout) ─────────────

export const CustomerSpecsTableLayout = ({
  title,
  showForm,
  onToggleForm,
  onSubmit,
  columns,
  formData,
  handleChange,
  children,
  rows,
  renderCell = null,
  getRowProps,
  hideActionsColumn = false,
  renderActions = () => null,
  sortConfig = null,
  onSort = () => {},
  searchValue = '',
  onSearchChange = () => {},
  isEditable = true,
  editingId = null,
  disableSearch = false,
  submitting = false,
  fitToViewport = false,
  viewportOffset = 140,
}) => {
  const [internalSearch, setInternalSearch] = React.useState(searchValue);
  const debounceRef = React.useRef(null);
  React.useEffect(() => setInternalSearch(searchValue), [searchValue]);
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange({ target: { value: internalSearch } }), 350);
    return () => clearTimeout(debounceRef.current);
  }, [internalSearch, onSearchChange]);

  const formRef = React.useRef(null);
  React.useEffect(() => {
    if (showForm && formRef.current) {
      const first = formRef.current.querySelector('input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (first && typeof first.focus === 'function') first.focus();
    }
  }, [showForm]);

  const visibleRows = rows.filter((row) => !(editingId && editingId === row.UniqueID && showForm));

  const tableRef = React.useRef(null);
  const [columnWidths, setColumnWidths] = React.useState(null);
  const [widthsFrozen, setWidthsFrozen] = React.useState(false);

  React.useEffect(() => {
    const isEditing = Boolean(isEditable && editingId);
    if (isEditing && !widthsFrozen) {
      const tableEl = tableRef.current;
      if (tableEl) {
        const firstRow = tableEl.querySelector('tbody tr');
        const cells = firstRow ? firstRow.children : tableEl.querySelectorAll('thead th');
        const widths = Array.from(cells).map((el) => Math.ceil(el.getBoundingClientRect().width));
        if (widths.length) {
          setColumnWidths(widths);
          setWidthsFrozen(true);
        }
      }
    } else if (!isEditing && widthsFrozen) {
      setColumnWidths(null);
      setWidthsFrozen(false);
    }
  }, [isEditable, editingId, widthsFrozen]);

  const containerStyle = fitToViewport ? {
    display: 'flex', flexDirection: 'column', height: `calc(100vh - ${viewportOffset}px)`, minHeight: 0,
  } : undefined;
  const wrapperStyle = fitToViewport ? { flex: '1 1 auto', minHeight: 0 } : undefined;

  const formatCellValue = (value, format) => {
    if (format === 'currency' && !isNaN(parseFloat(value))) return `$${parseFloat(value).toFixed(2)}`;
    return value ?? '';
  };

  return (
    <Container style={containerStyle}>
      <TableHeaderBox>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>{title}</h1>
          {children}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {isEditable && (
            <Button aria-label="Add Entry" onClick={onToggleForm} disabled={submitting}>
              {showForm ? 'Cancel' : 'Add Entry'}
            </Button>
          )}
          {!disableSearch && (
            <Input
              type="text"
              placeholder="Search..."
              aria-label="Search"
              value={internalSearch}
              onChange={(e) => setInternalSearch(e.target.value)}
              style={{ maxWidth: '240px' }}
            />
          )}
        </div>
      </TableHeaderBox>

      <form ref={formRef} onSubmit={onSubmit}>
        <TableWrapper style={wrapperStyle}>
          <Table ref={tableRef}>
            {columnWidths && (
              <colgroup>
                {columnWidths.map((w, i) => (
                  <col key={`col-${i}`} style={{ width: w, minWidth: w, maxWidth: w }} />
                ))}
              </colgroup>
            )}
            <thead>
              <tr>
                {columns.map(({ key, label }, idx) => (
                  <SortableHeader
                    key={key}
                    onClick={() => onSort(key)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Sort by ${label}`}
                    style={columnWidths ? { width: columnWidths[idx], minWidth: columnWidths[idx], maxWidth: columnWidths[idx] } : undefined}
                  >
                    {label}
                    {sortConfig?.key === key && (
                      <SortArrow direction={sortConfig.direction} />
                    )}
                  </SortableHeader>
                ))}
                {!hideActionsColumn && isEditable && (
                  <SortableHeader
                    style={columnWidths && columnWidths.length > columns.length
                      ? { width: columnWidths[columns.length], minWidth: columnWidths[columns.length], maxWidth: columnWidths[columns.length] }
                      : undefined}
                  >
                    Actions
                  </SortableHeader>
                )}
              </tr>
            </thead>

            <tbody>
              {showForm && isEditable && (
                <StyledTr $index={-1}>
                  {columns.map(({ key }, idx) => (
                    <Td key={key} style={columnWidths ? { width: columnWidths[idx], minWidth: columnWidths[idx], maxWidth: columnWidths[idx] } : undefined}>
                      {renderCell?.length >= 3 ? renderCell(key, formData, handleChange) : null}
                    </Td>
                  ))}
                  {!hideActionsColumn && (
                    <Td style={columnWidths && columnWidths.length > columns.length ? { width: columnWidths[columns.length], minWidth: columnWidths[columns.length], maxWidth: columnWidths[columns.length] } : undefined}>
                      <Button aria-label="Save" type="submit" disabled={submitting}>Submit</Button>
                    </Td>
                  )}
                </StyledTr>
              )}

              {visibleRows.map((row, index) => {
                const rowProps = { ...(getRowProps ? getRowProps(row, index) : {}), $index: index };
                const isEditingRow = isEditable && editingId && editingId === row.UniqueID;
                return (
                  <StyledTr key={row.UniqueID} {...rowProps}>
                    {columns.map(({ key, format, numeric, center }, idx) => (
                      <Td
                        key={key}
                        numeric={numeric}
                        style={{
                          ...(columnWidths ? { width: columnWidths[idx], minWidth: columnWidths[idx], maxWidth: columnWidths[idx] } : {}),
                          ...(center ? { textAlign: 'center' } : numeric ? { textAlign: 'right' } : { textAlign: 'left' })
                        }}
                      >
                        {isEditingRow
                          ? renderCell?.length >= 3
                            ? renderCell(key, row, handleChange)
                            : formatCellValue(row[key], format)
                          : renderCell?.length === 2
                          ? renderCell(key, row)
                          : formatCellValue(row[key], format)}
                      </Td>
                    ))}
                    {!hideActionsColumn && isEditable && (
                      <Td style={columnWidths && columnWidths.length > columns.length ? { width: columnWidths[columns.length], minWidth: columnWidths[columns.length], maxWidth: columnWidths[columns.length] } : undefined}>
                        {renderActions(row)}
                      </Td>
                    )}
                  </StyledTr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </form>
    </Container>
  );
};

export const sharedScrollStyle = css`
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb {
    background-color: ${colors.shadowHeavy};
    border-radius: 4px;
    border: 2px solid ${colors.border};
  }
  &::-webkit-scrollbar-track { background: transparent; }
  scrollbar-width: thin;
  scrollbar-color: ${colors.shadowHeavy} transparent;
`;

export const PageWrapper = styled.div`
  display: flex;
  max-height: 100%;
  overflow: hidden;
  background: ${colors.background};
  backdrop-filter: ${colors.backdrop};
  border-radius: 20px;
  border: 1px solid ${colors.border};
  box-shadow: 0 8px 32px ${colors.shadow};
  box-sizing: border-box;
  position: relative;
`;

export const FiltersContainer = styled.div`
  width: 260px;
  padding: 1rem;
  border-radius: 16px 0 0 16px;
  background: ${colors.background};
  backdrop-filter: blur(16px);
  border-right: 1px solid var(--white-a15);
  border-top: 1px solid var(--white-a15);
  border-bottom: 1px solid var(--white-a15);
  box-shadow: 2px 0 10px ${colors.shadow};
  transition: all 0.3s ease;
  overflow-y: auto;
  max-height: 100vh;
  overscroll-behavior: contain;
  ${sharedScrollStyle}
`;

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1rem;
  height: 93vh;
  Width: 100vh;
  box-sizing: border-box;
`;

export const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: var(--white-a15);
  border-radius: 12px;
  border: 1px solid var(--white-a20);
  box-shadow: 0 6px 18px var(--shadow-a08);
`;

export const ScrollableContent = styled.div`
  flex: 1;
  z-index: 1;
  overflow-y: auto;
  overflow-x: auto;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  min-height: 0;
  ${sharedScrollStyle}
`;

export const Spinner = styled.div`
  margin: 2rem auto;
  border: 4px solid var(--danger-a15);
  border-top: 4px solid var(--black);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export const NoResultsMessage = styled.div`
  font-size: 1rem;
  color: var(--grey-500);
  margin: 2rem auto;
  text-align: center;
`;

export const SkeletonCard = styled.div`
  position: relative;
  height: 250px;
  border-radius: 16px;
  background: var(--grey-200);
  overflow: hidden;
  backdrop-filter: blur(8px);
  &::after {
    content: '';
    position: absolute;
    top: 0; left: -150px; height: 100%; width: 150px;
    background: linear-gradient(to right, transparent 0%, var(--white-a55) 50%, transparent 100%);
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer { 100% { transform: translateX(100%); } }
`;

export const Label = styled.div`
  font-size: 0.95rem;
  margin: 0.5rem 0 0.25rem;
  font-weight: 700;
  color: var(--green-800);
`;

export const StyledInput = styled.input`
  width: 100%; padding: 0.5rem 0.75rem;
  border-radius: 10px; background: var(--white-a15);
  border: 1px solid var(--white-a20);
  color: var(--shadow-a80); font-size: 0.9rem; outline: none;
  &::placeholder { color: var(--shadow-a45); }
  &:focus { border: 2px solid var(--orange-500); box-shadow: 0 0 0 1px var(--orange-a12); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const StyledSelect = styled.select`
  width: 100%; padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  border-radius: 10px; border: 1px solid var(--white-a20);
  background: var(--white-a15);
  color: var(--shadow-a80); font-size: 0.9rem; outline: none; cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus { border: 2px solid var(--orange-500); box-shadow: 0 0 0 1px var(--orange-a12); }
`;

// Filter dropdown select used on the Index filter panel (kept separate from TableStyles select)
export const FilterSelect = styled.select`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--white-a20);
  background: var(--white-a15);
  color: var(--shadow-a80);
  font-size: 0.9rem;
  outline: none;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus { border: 2px solid var(--orange-500); box-shadow: 0 0 0 1px var(--orange-a12); }
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
`;

export const CardWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: var(--white-a15);
  box-shadow: 0 4px 10px var(--shadow-a08);
  backdrop-filter: blur(12px);
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  &:hover { transform: translateY(-3px); box-shadow: 0 8px 18px var(--shadow-a08); }
`;

export const ImageWrapper = styled.div`
  width: 100%;
  height: ${({ $short }) => ($short ? '200px' : '220px')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

export const InfoSection = styled.div`
  padding: 0.75rem 1rem; color: var(--black); font-size: 1rem; font-weight: 700; text-align: center; background: var(--white-a40);
`;

export const Pill = styled.span`
  display: inline-flex; align-items: center; padding: 0.3rem 0.7rem; border-radius: 999px; font-size: 0.85rem; font-weight: 800; color: var(--white);
  background: ${({ $bg }) => $bg}; border: 2px solid ${({ $border }) => $border}; box-shadow: 0 2px 6px var(--shadow-a15); user-select: none; max-width: 100%;
`;

export const UniversalHeader = styled.div`
  width: 100%;
  background: ${colors.orange};
  color: var(--white);
  text-align: center;
  font-weight: 800;
  font-size: 0.9rem;
  padding: 6px 8px;
  line-height: 1;
`;


// ─── Shared form controls for Customer Specs (scoped here only) ───────────────

export const SizeTag = styled.span`
  background: var(--green-800);
  color: var(--white);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85rem;
  white-space: nowrap;
  border: none; /* so a removable pill can render as="button" and stay keyboard-operable */
`;


// ─── Grade split-pill (grade coloring plan, M4) ───────────────────────────────
// Two-zone pill: grey value zone | grade-chip tray, with a chevron that opens the
// GradeMultiSelect popover. A single background can't express the split, so the
// shell is a flex container of separate children clipped by overflow:hidden.
export const GradePillShell = styled.button`
  display: inline-flex;
  align-items: stretch;
  padding: 0;
  border: 2px solid ${(p) => (p.$invalid ? 'var(--color-danger)' : 'var(--grey-700)')};
  border-radius: 999px;
  overflow: hidden;
  background: var(--grey-600);
  box-shadow: ${(p) => (p.$invalid ? '0 0 0 3px var(--danger-a30)' : '0 2px 6px var(--shadow-a15)')};
  max-width: 100%;
  font: inherit;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
  &:hover {
    box-shadow: ${(p) => {
    if (p.$invalid) return '0 0 0 3px var(--danger-a30)';
    return p.$disabled ? '0 2px 6px var(--shadow-a15)' : '0 3px 8px var(--shadow-a15)';
  }};
    filter: ${(p) => (p.$disabled ? 'none' : 'brightness(1.04)')};
  }
  &:disabled { opacity: 1; }
`;

export const GradePillValue = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  background: var(--grey-600);
  color: var(--white);
  font-size: 0.85rem;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 14ch;
`;

export const GradePillGrades = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0.45rem;
  background: var(--white-a90);
  min-width: 0;
`;

export const GradeChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  max-width: 12ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: ${(p) => (p.$neutral ? 'var(--grey-200)' : p.$bg)};
  border: 1px solid ${(p) => (p.$neutral ? 'var(--shadow-a08)' : p.$border)};
  color: ${(p) => (p.$neutral ? 'var(--grey-700)' : 'var(--white)')};
`;

export const GradePillChevron = styled.span`
  display: inline-flex;
  align-items: center;
  background: var(--grey-600);
  color: var(--white);
  opacity: 0.75;
  padding: 0 0.5rem 0 0.3rem;
  ${GradePillShell}:hover & { opacity: 1; }
  svg { display: block; }
`;

export const Button = styled.button`
  background-color: ${tableColors.header};
  color: var(--white);
  border: none;
  padding: 5px 12px;
  font-size: 0.75rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  font-family: var(--font-body);
  font-weight: 500;
  cursor: pointer;
  width: auto;
  min-width: 60px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const DangerButton = styled(Button)`
  background-color: var(--red-600);
  margin-left: 0.5rem;
  &:hover { background-color: var(--color-danger); }
`;

export const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--white-a66);
  border-radius: 8px;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--black);
  font-family: var(--font-body);
  transition: all 0.2s ease;
  border-color: ${({ $error }) => ($error ? 'var(--color-danger)' : 'var(--white-a66)')};
  box-shadow: ${({ $error }) => ($error ? '0 0 0 1px var(--color-danger)' : 'none')};
  &:focus { outline: none; border: 1px solid var(--green-800); box-shadow: 0 0 0 1px var(--green-800); }
  ${({ $size }) => ($size ? css`
    @media (min-width: 900px) {
      max-width: ${$size === 'sm' ? '260px' : ($size === 'md' ? '420px' : '100%')};
    }
  ` : '')}
`;

export const Select = styled.select.attrs(props => ({
  name: props.name,
  value: props.value,
  onChange: props.onChange
}))`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--white-a66);
  border-radius: 8px;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--black);
  font-family: var(--font-body);
  transition: all 0.2s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  border-color: ${({ $error }) => ($error ? 'var(--color-danger)' : 'var(--white-a66)')};
  box-shadow: ${({ $error }) => ($error ? '0 0 0 1px var(--color-danger)' : 'none')};
  &:focus { outline: none; border: 1px solid var(--green-800); box-shadow: 0 0 0 1px var(--green-800); }
  ${({ $size }) => ($size ? css`
    @media (min-width: 900px) {
      max-width: ${$size === 'sm' ? '260px' : ($size === 'md' ? '420px' : '100%')};
    }
  ` : '')}
`;

// ─── Searchable Dropdown (combobox-like) ─────────────────────────────────────────
export const ComboWrap = styled.div`
  position: relative;
`;

export const ComboButton = styled.button`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${({ $error }) => ($error ? 'var(--color-danger)' : 'var(--white-a66)')};
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--color-text);
  font-size: 0.9rem;
  font-family: var(--font-body);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  box-shadow: ${({ $error }) => ($error ? '0 0 0 1px var(--color-danger)' : 'none')};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus { outline: none; border: 1px solid var(--green-800); box-shadow: 0 0 0 1px var(--green-800); }
`;

export const ComboCaret = styled.span`
  margin-left: 8px;
  opacity: 0.6;
`;

export const ComboMenu = styled.div`
  position: absolute;
  z-index: 5000;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--shadow-a15);
  border-radius: 10px;
  box-shadow: 0 8px 24px var(--shadow-a15);
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ComboSearch = styled.input`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--shadow-a15);
  margin-bottom: 8px;
  font-size: 0.9rem;
  &:focus { outline: none; border-color: var(--green-800); box-shadow: 0 0 0 1px var(--green-800); }
`;

export const ComboList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  border-radius: 8px;
  min-height: 0;
`;

export const ComboOption = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: ${({ $active }) => ($active ? 'var(--green-a08)' : 'transparent')};
  color: var(--black);
  border: none;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: var(--shadow-a05); }
`;

export const ClearButton = styled.button`
  padding: 6px 12px;
  border-radius: 10px;
  border: 1px solid var(--shadow-a15);
  font-weight: 800;
  font-size: 0.85rem;
  line-height: 1;
  transition:
    background-color 220ms ease,
    border-color 220ms ease,
    color 220ms ease,
    box-shadow 220ms ease,
    transform 220ms ease,
    opacity 180ms ease;
  background: ${({ $active }) => ($active ? colors.green : 'var(--shadow-a08)')};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--shadow-a55)')};
  box-shadow: ${({ $active }) => ($active ? '0 6px 16px var(--green-a15)' : 'none')};
  cursor: ${({ $active }) => ($active ? 'pointer' : 'not-allowed')};
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  &:hover {
    ${({ $active }) => $active ? css`transform: translateY(-1px); box-shadow: 0 8px 20px var(--green-a15);` : ''};
  }
  &:active {
    ${({ $active }) => $active ? css`transform: translateY(0px) scale(0.99);` : ''};
  }
  &:focus {
    outline: none;
    box-shadow: ${({ $active }) => ($active ? '0 8px 20px var(--green-a15)' : 'none')};
    border-color: ${({ $active }) => ($active ? colors.orange : 'var(--shadow-a15)')};
  }
  &:disabled {
    pointer-events: none;
  }
`;

export const TextArea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--white-a66);
  border-radius: 8px;
  background: var(--white-a66);
  backdrop-filter: blur(8px);
  color: var(--black);
  font-family: var(--font-body);
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  border-color: ${({ $error }) => ($error ? 'var(--color-danger)' : 'var(--white-a66)')};
  box-shadow: ${({ $error }) => ($error ? '0 0 0 1px var(--color-danger)' : 'none')};
  &:focus { outline: none; border: 1px solid var(--green-800); box-shadow: 0 0 0 1px var(--green-800); }
`;

export const PageHeader = styled.div`
  background: var(--green-a60);
  backdrop-filter: blur(8px);
  color: var(--white);
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 12px var(--shadow-a15);
`;

export const SectionContainer = styled.div`
  background-color: var(--white-a25);
  border-radius: 12px;
  backdrop-filter: blur(8px);
  padding: 1rem 1.5rem;
  margin-bottom: 1rem;
  font-family: var(--font-body);
`;

// ─── Customer Specs Entry Form layout primitives ────────────────────────────────
export const FormShell = styled.div`
  width: 100%;
  max-width: 1560px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 680px) {
    padding: 0.75rem;
  }
`;

export const FormSectionHeader = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--shadow-a80);
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px 16px;
  align-items: start;

  @media (min-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

// Two-up grid for the Sizing section: Recommended | Spec, Subbing | Approval,
// Notes full-width. Collapses to a single column as the viewport narrows.
export const SizingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px 16px;
  align-items: start;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const FieldLabel = styled.div`
  font-weight: 650;
  font-size: 0.92rem;
  color: var(--shadow-a80);
`;

export const FieldHint = styled.div`
  font-size: 0.82rem;
  color: var(--shadow-a55);
`;

export const FieldError = styled.div`
  color: var(--color-danger);
  font-size: 0.85rem;
`;

export const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

// Consistent grid for question blocks in the form
export const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px; /* even spacing between questions */
  align-items: start;
`;

// Fullscreen lightbox for images
export const LightboxBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: var(--shadow-a65);
  display: flex; align-items: center; justify-content: center;
`;
export const LightboxImage = styled.img`
  max-width: min(95vw, 100%);
  max-height: 80vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 12px 40px var(--shadow-a35);
`;


// ─── Index Drawer Panel (scoped to CustomerSpecs) ──────────────────────────────

export const IndexPanelBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2000;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

export const IndexPanelContainer = styled.div`
  width: 100%;
  height: 100%;
  background: var(--cream-500);
  box-shadow: 0 12px 28px var(--shadow-a15);
  border: 1px solid var(--shadow-a08);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  position: relative;
  backdrop-filter: none;
`;

export const IndexPanelContent = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  ${sharedScrollStyle}
`;

export const ColumnGrid = styled.div`
  --col-min: 360px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(var(--col-min), 1fr));
  gap: 12px;
  align-items: stretch;
  height: 100%;
  grid-auto-rows: 1fr;
`;

export const ColumnCard = styled.div`
  width: 100%;
  border-radius: 12px;
  background: var(--white-a40);
  border: 1px solid var(--shadow-a05);
  padding: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

// ─── Sub-group cards for columns ───────────────────────────────────────────────
export const GroupCard = styled.div`
  border-radius: 12px;
  background: var(--white-a55);
  overflow: hidden;
  border: 1px solid var(--shadow-a05);
  box-shadow: 0 2px 8px var(--shadow-a05);
`;

export const GroupHeaderBar = styled.div`
  background: ${colors.green};
  color: var(--white);
  font-weight: 800;
  text-align: center;
  padding: 6px 10px;
  font-size: 0.88rem;
`;

export const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
`;

export const TableRow = styled.div`
  display: contents;
  /* Alternating background on row basis using :has selector fallback via nth-of-type pattern */
  & > div {
    padding: 6px 10px;
    border-bottom: 1px solid var(--shadow-a08);
    background: var(--white);
  }
  &:nth-of-type(even) > div {
    background: var(--shadow-a05);
  }
`;

export const CellKey = styled.div`
  font-weight: 700;
`;

export const CellValue = styled.div`
  text-align: right;
  white-space: pre-wrap;
`;

export const ValuePillWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: ${(p) => (p.$left ? 'flex-start' : 'flex-end')};
  align-items: center;
  padding-left: ${(p) => (p.$left ? '0' : '12px')};
`;

export const ValuePill = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${colors.green};
  color: var(--white);
  font-weight: 800;
  font-size: 0.85rem;
  line-height: 1.05;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  cursor: default;
`;

export const DividerRow = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 5px 10px;
  font-weight: 700;
  background: var(--white-a80);
  border-bottom: 1px solid var(--shadow-a08);
`;

export const NotesArea = styled.div`
  grid-column: 1 / -1;
  padding: 10px 12px;
  background: var(--grey-200);
  color: var(--color-text);
  min-height: 0;
  border-top: 0;
`;

export const FirstSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const LogoBox = styled.div`
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--white-a90);
  border: 1px solid var(--shadow-a08);
  border-radius: 12px;
  padding: 10px;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const GreyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--grey-700);
  background: var(--grey-200);
  border: 1px solid var(--shadow-a08);
`;

export const GreenTitle = styled.div`
  background: ${colors.green};
  color: var(--white);
  font-weight: 800;
  text-align: center;
  padding: 7px 10px;
  border-radius: ${({ $withHeader }) => ($withHeader ? '0 0 10px 10px' : '10px')};
  font-size: 0.95rem;
`;

export const IndexPanelFooter = styled.div`
  padding: 0 12px 5px;
  border-top: 1px solid var(--shadow-a05);
  text-align: center;
`;

export const FooterDate = styled.div`
  font-weight: 600;
`;

export const FooterEditor = styled.div`
  color: var(--shadow-a80);
`;

export const PanelCloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background: ${colors.orange};
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: var(--white);
  box-shadow: 0 2px 6px var(--shadow-a15);
  line-height: 1;
`;

export const SpecNameGroup = styled.div`
  border-radius: 10px;
  overflow: hidden;
`;

// ─── Spec Index detail-panel mini-cards (detail-panel redesign, M3) ───────────
// Read-only relationship cards for Style/Label/Method entries: item name dominant,
// assigned grade chips as small secondary metadata below. Popup-exclusive; the
// editor split-pills (GradePill*) are intentionally untouched.
export const AssignedTier = styled.div`
  grid-column: 1 / -1;
  padding: 7px 12px;
  border-bottom: 1px solid var(--shadow-a08);
  background: var(--white);
`;

export const AssignedTierLabel = styled.div`
  font-weight: 700;
  font-size: 0.8rem;
  color: ${colors.green};
  margin-bottom: 5px;
`;

export const AssignedItemsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const AssignedCard = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 4px 9px;
  border-radius: 10px;
  background: var(--white);
  border: 1px solid var(--shadow-a15);
  box-shadow: 0 1px 3px var(--shadow-a08);
  max-width: 100%;
  font: inherit;
  text-align: left;
  cursor: ${(p) => (p.$clickable ? 'zoom-in' : 'default')};
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--color-focus-ring); }
  &:hover { box-shadow: ${(p) => (p.$clickable ? '0 2px 7px var(--shadow-a15)' : '0 1px 3px var(--shadow-a08)')}; }
`;

export const AssignedCardName = styled.span`
  font-weight: 800;
  font-size: 0.8rem;
  color: var(--color-text);
  line-height: 1.15;
  white-space: normal;
  word-break: break-word;
`;

export const AssignedCardChips = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const AssignedGradeChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
  background: ${(p) => (p.$neutral ? 'var(--grey-200)' : p.$bg)};
  border: 1px solid ${(p) => (p.$neutral ? 'var(--shadow-a08)' : p.$border)};
  color: ${(p) => (p.$neutral ? 'var(--grey-700)' : 'var(--white)')};
`;